import { Box, IconButton, CircularProgress } from "@mui/material";
import { ArrowForward, SaveOutlined as SaveIcon } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useIsMobile from "../hooks/useIsMobile";
import { RTL } from "../cache/CacheRtl";
import CalculatorPanel from "../componenets/nutrition/v2/calculator/CalculatorPanel";
import {
  defaultCalculatorState,
  deriveTargets,
  isCalculable,
  type CalculatorState,
} from "../componenets/nutrition/v2/calculator/calculatorState";
import TargetsSummaryBar from "../componenets/nutrition/v2/builder/TargetsSummaryBar";
import TargetsMiniStrip from "../componenets/nutrition/v2/builder/TargetsMiniStrip";
import MealsList from "../componenets/nutrition/v2/builder/MealsList";
import { useStickyHeaderHeight } from "../componenets/nutrition/v2/builder/useStickyHeaderHeight";
import { useScrollPageToTop } from "../componenets/nutrition/v2/builder/useScrollPageToTop";
import ConfirmDialog from "../componenets/nutrition/v2/dialogs/ConfirmDialog";
import { BuilderToast, InlineEdit, PrimaryButton, TextButton } from "../componenets/nutrition/v2/ui/primitives";
import { v2 } from "../componenets/nutrition/v2/ui/v2Tokens";
import { energyFigure, macroFigure } from "../componenets/nutrition/v2/ui/nutritionUnits";
import { useNutritionDisplayMode } from "../contexts/NutritionDisplayContext";
import { saveMenuTemplate } from "../services/NutritionV2Service";
import { useMenuTemplateV2 } from "../componenets/nutrition/v2/data/useNutritionV2";
import {
  emptyMacros,
  listTotals,
  sameMacros,
  targetsFromMeals,
  type MacroSet,
  type MealV2,
  type MenuTemplateV2,
} from "../componenets/nutrition/v2/model/nutritionV2Model";

const TEMPLATES = "/app/menu-templates";

const f1 = (n: number) => Math.round(n * 10) / 10;

/**
 * The targets as figures and their units, in the company's unit. Number and unit come from one
 * helper call, so a points figure can never sit beside `גר׳`. Fibre has no point value and stays
 * in grams in both modes.
 */
const GoalFigures = ({ targets }: { targets: MacroSet }) => {
  const mode = useNutritionDisplayMode();
  const energy = energyFigure(targets.calories, mode);
  const [protein, carbs, fats] = (["protein", "carbs", "fats"] as const).map((key) =>
    macroFigure(key, targets[key], mode)
  );
  return (
    <>
      <b>{energy.text}</b> {energy.unit} · <b>{protein.text}</b> {protein.unit} חלבון ·{" "}
      <b>{carbs.text}</b> {carbs.unit} פחמימות · <b>{fats.text}</b> {fats.unit} שומן ·{" "}
      <b>{f1(targets.fiber)}</b> גר׳ סיבים
    </>
  );
};

/** What the editor holds. The server's `createdDate` is not editable, so it does not travel. */
type TemplateDraft = {
  id: string;
  name: string;
  targets: MacroSet;
  qualityProteinPct: number;
  /**
   * Opaque to the server, which stores and returns it verbatim — so the client's shape is the only
   * definition of it, and narrowing it here is what lets the page read `skipped` off a template
   * without casting at every use.
   */
  calculator: CalculatorState | null;
  meals: MealV2[];
};

/**
 * Whether this template has ever had numbers put on it. Every field is nullable, and a template
 * migrated from V1 comes back as a MacroSetDto of all-nulls rather than as an absent object — so
 * "has targets" cannot be a truthiness check on `targets` itself.
 */
const stripNulls = (macros: MacroSet): Partial<MacroSet> =>
  Object.fromEntries(Object.entries(macros).filter(([, v]) => v != null)) as Partial<MacroSet>;

const hasTargets = (template: MenuTemplateV2): boolean =>
  Boolean(template.targets) &&
  Object.values(template.targets as Record<string, number | null>).some((v) => Number(v) > 0);

/** The trainer said, in as many words, that this template is not built from a calculation. */
const skippedCalculator = (template: MenuTemplateV2): boolean =>
  (template.calculator as CalculatorState | null)?.skipped === true;

const draftOf = (template: MenuTemplateV2): TemplateDraft => ({
  id: template.id,
  name: template.name,
  // nulls throughout on a migrated template — coerced so nothing downstream does arithmetic on null
  targets: template.targets ? { ...emptyMacros(), ...stripNulls(template.targets) } : emptyMacros(),
  qualityProteinPct: template.qualityProteinPct ?? 0,
  calculator: (template.calculator as CalculatorState | null) ?? null,
  meals: template.meals,
});

type EditorProps = {
  /** absent for a new template */
  existing?: MenuTemplateV2;
};

/**
 * A template is built the way a menu is: the calculator first, then the meal builder aimed at what
 * it produced. `MealsList` is the whole of the builder — food search, quantities, substitution
 * groups, alternative meals — and the calculator gives the trainer the number to build against.
 *
 * The targets are the TEMPLATE's own and are stored on it, so it reopens knowing what it was built
 * for. They are not pushed onto a menu created from it: that menu's calculator prefills from the
 * trainee's real weight, activity and program, which beats a template's guess.
 *
 * What a template still does not have is everything written for one person — the weekday selection,
 * supplements, the water goal and the note. Those are a prescription, not a shape.
 */
const MenuTemplateBuilderEditor = ({ existing }: EditorProps) => {
  const navigate = useNavigate();

  const [template, setTemplate] = useState<TemplateDraft>(() =>
    existing
      ? draftOf(existing)
      : { id: "", name: "תבנית חדשה", targets: emptyMacros(), qualityProteinPct: 0, calculator: null, meals: [] }
  );
  const [calculator, setCalculator] = useState<CalculatorState>(
    () => (existing?.calculator as CalculatorState) || defaultCalculatorState()
  );

  // Step 2 appears once the trainer confirms the calculator, exactly as on a new menu. There is no
  // trainee to prefill from, so it opens on its defaults and the trainer types the numbers.
  //
  // An EXISTING template opens straight into the builder — except when it has no targets, which is
  // every one of the 599 migrated from V1: those never held any. Showing them a summary bar reading
  // "0 קק״ל" with the calculator folded away behind a link answers a question nobody asked. The
  // calculator is the useful thing to put in front of a template that has never had numbers.
  //
  // A template that SKIPPED the calculator is the exception to the exception: it has no targets on
  // purpose — and an empty one has none to show either — so putting the calculator in front of it
  // would re-ask the question its trainer already answered.
  const [builderOpen, setBuilderOpen] = useState(Boolean(existing));
  const [calculatorOpen, setCalculatorOpen] = useState(
    !existing || (!hasTargets(existing) && !skippedCalculator(existing))
  );
  // what the calculator held when it was reopened, so "ביטול" can put it back
  const [calculatorSnapshot, setCalculatorSnapshot] = useState<CalculatorState | null>(null);
  const [notice, setNotice] = useState("");

  /**
   * This template was built WITHOUT a calculation, so its own meals are the recommendation: the
   * targets are held equal to what is in it as the trainer builds, and nothing ever reads as over
   * or under.
   *
   * Read off the SAVED calculator rather than the panel's live state, so reopening the template
   * resumes the same behaviour — and so a template written before any of this existed (no stored
   * calculator, or one with no such flag) keeps its calculated targets untouched. That is the same
   * blob the menu builder rides this on; see `CalculatorState.skipped`.
   */
  const targetsFollowTemplate = template.calculator?.skipped === true;

  // The page header sticks at the top; everything that sticks beneath it needs its height, and
  // that height changes as the header's rows wrap. Measured, not assumed.
  const [headerRef, headerHeight] = useStickyHeaderHeight();
  const [pageRef, scrollToTop] = useScrollPageToTop();
  // structural: the mini strip exists only on a phone — see the note on MenuBuilderV2Page
  const isMobile = useIsMobile();

  const derived = useMemo(() => deriveTargets(calculator), [calculator]);
  const totals = useMemo(() => listTotals(template.meals.flatMap((meal) => meal.items)), [template.meals]);

  /**
   * Follow mode's one job: keep the targets equal to what is in the template.
   *
   * Written into `targets` rather than merely displayed, so the stored template carries a real set
   * of numbers — and so a menu started from it is not seeded off zeroes. The equality guard is what
   * keeps opening such a template from looking like an edit: the stored targets already equal the
   * totals, so there is nothing to set.
   */
  useEffect(() => {
    if (!targetsFollowTemplate) return;
    setTemplate((prev) => {
      const fromMeals = targetsFromMeals(prev);
      return sameMacros(prev.targets, fromMeals) ? prev : { ...prev, targets: fromMeals };
    });
  }, [targetsFollowTemplate, template.meals]);

  /**
   * The calculator is a RECOMMENDATION and does not continuously overwrite the template's targets —
   * a trainer may set them by hand and those edits are the truth. It writes only when explicitly
   * confirmed: creating the template, or "החל יעדים" after reopening it.
   */
  const withCalculatorTargets = (prev: TemplateDraft): TemplateDraft => ({
    ...prev,
    targets: derived.targets,
    // Written explicitly, not left out: this is also the way OUT of follow mode, and a template
    // built without a calculator has to stop tracking its meals the moment it is given one.
    calculator: { ...calculator, skipped: false },
  });

  /**
   * The other way to answer "what should this template aim at": whatever is in it.
   *
   * Used both by the trainer who walks past the calculator on the way in and by the one who decides,
   * halfway through building, that the targets are not the point — from then on the template's own
   * meals are the recommendation and follow every edit.
   *
   * The template's STORED calculator is kept, not the panel's live state: a trainer who opened the
   * calculator, changed a number and closed it without applying has not applied it, and this must
   * not be the write that does. Only where there is none does the panel's state fill in, so that
   * "פתח מחשבון" later opens on something.
   */
  const withTemplateAsTargets = (prev: TemplateDraft): TemplateDraft => ({
    ...prev,
    targets: targetsFromMeals(prev),
    calculator: { ...(prev.calculator ?? calculator), skipped: true },
  });

  // Saving is explicit. A NEW template baselines at "" so it starts dirty and can be saved at all —
  // there is nothing on the server yet to compare it against.
  const [savedSnapshot, setSavedSnapshot] = useState<string>(() =>
    existing ? JSON.stringify(draftOf(existing)) : ""
  );
  const isDirty = JSON.stringify(template) !== savedSnapshot;

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  /** Writes `next` and adopts whatever the server stored. Returns whether it landed. */
  const persist = async (next: TemplateDraft): Promise<boolean> => {
    setSaving(true);
    setSaveError("");
    try {
      // The builder ADOPTS the server's answer rather than keeping what it sent — that is what turns
      // the client's temporary uids into real ids, and what gives a brand-new template its id so the
      // next save updates it instead of creating a second copy.
      const adopted = draftOf(await saveMenuTemplate(next));
      setTemplate(adopted);
      setSavedSnapshot(JSON.stringify(adopted));
      return true;
    } catch (e) {
      // Never silently: the only copy of this template is in this tab. Leaving the snapshot alone
      // keeps it dirty, so "שמור תבנית" retries.
      setSaveError("השמירה נכשלה. התבנית לא נשמרה — נסה שוב.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const ok = await persist(template);
    if (ok) setNotice("התבנית נשמרה");
    return ok;
  };

  /** The calculator's exit on a NEW template: this is where it is first written to the server. */
  const createTemplateStep = async (options: { skipCalculator?: boolean } = {}) => {
    // A calculator that was skipped outright and one that was never filled in mean the same thing —
    // the trainer is not working from a calculation — so they get the same template. Without this,
    // pressing "בנה תבנית" on an untouched panel wrote targets derived from the formula's 70kg
    // fallback body, which is a number about nobody.
    const skipped = Boolean(options.skipCalculator) || !isCalculable(calculator);
    const next = skipped ? withTemplateAsTargets(template) : withCalculatorTargets(template);
    setTemplate(next);
    setBuilderOpen(true);
    setCalculatorOpen(false);
    // The builder still opens with the targets just confirmed even if the write fails — losing them
    // would send the trainer back through the whole calculator. It is simply unsaved, which the
    // header says, and the save button retries.
    await persist(next);
  };

  // The way back into the calculator sits in the sticky targets bar, on screen from anywhere in the
  // template — the calculator itself is at the top. Bring the page back to it.
  const openCalculator = () => {
    setCalculatorSnapshot(calculator);
    setCalculatorOpen(true);
    scrollToTop();
  };

  const applyCalculator = () => {
    setTemplate(withCalculatorTargets);
    setCalculatorSnapshot(null);
    setCalculatorOpen(false);
  };

  const cancelCalculator = () => {
    if (calculatorSnapshot) setCalculator(calculatorSnapshot);
    setCalculatorSnapshot(null);
    setCalculatorOpen(false);
  };

  /**
   * "Stop counting me against a number" — the same state a trainer gets by walking past the
   * calculator, reachable from inside a template that already has one.
   *
   * A MODE rather than a one-shot: the targets do not merely take the template's current contents,
   * they become them and go on following every later edit. The way back is the calculator itself —
   * confirming it hands the targets back to the recommendation.
   */
  const ignoreTargets = () => {
    setTemplate(withTemplateAsTargets);
    setNotice("היעדים ייגזרו מעכשיו מהתבנית עצמה");
  };

  const [leaveOpen, setLeaveOpen] = useState(false);
  const backToTemplates = () => navigate(TEMPLATES);
  const requestLeave = () => (isDirty ? setLeaveOpen(true) : backToTemplates());

  const goalSummary = (
    <Box
      component="span"
      data-testid="template-goal-summary"
      sx={{ "& b": { fontSize: "14px", fontWeight: 600, color: v2.ink, letterSpacing: "-.01em" } }}
    >
      <GoalFigures targets={template.targets} />
    </Box>
  );

  const editCalculatorLink = (
    <TextButton
      quiet
      tone="green"
      onClick={openCalculator}
      sx={{ height: { xs: "40px", md: "30px" }, padding: "0 8px", fontSize: "13px" }}
    >
      {/* A template built without one has no calculation to "edit" — the same door, named for what
          is behind it in each case. The 599 migrated from V1 are all of the second kind. */}
      {targetsFollowTemplate || !template.calculator ? "פתח מחשבון" : "ערוך מחשבון"}
    </TextButton>
  );

  /**
   * The head of the targets bar: where the recommendation came from, and how to change that.
   *
   * In follow mode there is nothing to press — the targets already are the template — so the chip
   * says so and the only offer is the calculator. Otherwise the trainer can hand the goal over to
   * what they have actually built.
   */
  const targetsActions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
      {targetsFollowTemplate ? (
        <Box
          data-testid="targets-from-template-chip"
          title="הערכים המומלצים הם מה שיש בתבנית. לחזרה ליעדים מחושבים — פתח מחשבון והחל יעדים"
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            whiteSpace: "nowrap",
            padding: "2px 10px",
            borderRadius: "20px",
            color: v2.brand,
            background: v2.brandWash,
          }}
        >
          נגזר מהתבנית
        </Box>
      ) : (
        <TextButton
          quiet
          onClick={ignoreTargets}
          data-testid="ignore-targets"
          title="התבנית תפסיק להיספר מול היעדים — הערכים המומלצים יהיו מה שיש בה"
          sx={{ height: { xs: "40px", md: "30px" }, padding: "0 8px", fontSize: "13px" }}
        >
          התעלם מיעדים
        </TextButton>
      )}
      {editCalculatorLink}
    </Box>
  );

  return (
    <RTL>
      <Box
        ref={pageRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          paddingBottom: "60px",
          maxWidth: "1200px",
          marginInline: "auto",
          width: "100%",
        }}
      >
        {/* Same treatment as the menu builder's header — see the long note there. This one carries
            no day or type controls, so nothing has to move; it just stops spending four wrapped
            rows of a phone screen on a title. */}
        <Box
          ref={headerRef}
          data-testid="template-header"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 30,
            /* off on a phone, where `<main>`'s overflowX:hidden clips it anyway; above md it keeps
               the 24px it has always had — see the same note on MenuBuilderV2Page */
            marginInline: { xs: 0, md: "-24px" },
            paddingInline: { xs: 0, md: "24px" },
            background: "rgba(246,248,250,.88)",
            backdropFilter: "saturate(180%) blur(12px)",
            borderBottom: `1px solid ${v2.line}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: "8px", md: "16px" },
              rowGap: "8px",
              padding: { xs: "8px 0 6px", md: "18px 0 14px" },
              flexWrap: "wrap",
            }}
          >
            <IconButton
              aria-label="חזרה"
              onClick={requestLeave}
              sx={{
                color: v2.ink3,
                p: { xs: 1, md: 0.75 },
                borderRadius: v2.rSm,
                flex: { xs: "none", md: "0 1 auto" },
                "&:hover": { bgcolor: v2.lineSoft, color: v2.ink },
              }}
            >
              <ArrowForward />
            </IconButton>

            <InlineEdit
              size="lg"
              ariaLabel="שם התבנית"
              value={template.name}
              onChange={(name) => setTemplate((prev) => ({ ...prev, name }))}
              // 340 is wider than the whole page on a phone; there it simply takes the room the
              // chip and the status words give up
              sx={{ maxWidth: { xs: "none", md: "340px" }, flex: { xs: "1 1 auto", md: "0 1 auto" }, minWidth: 0 }}
            />

            {/* what this thing IS — worth a row on a desktop, not worth one of four on a phone,
                where the page it was reached from already said so */}
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                fontSize: "12px",
                fontWeight: 500,
                whiteSpace: "nowrap",
                padding: "3px 10px",
                borderRadius: "20px",
                color: v2.ink2,
                background: v2.lineSoft,
              }}
            >
              תבנית חברה
            </Box>

            {builderOpen && (
              <>
                <Box
                  /* mobile-only, so a desktop screen reader hears exactly what it heard before —
                     see the same note on MenuBuilderV2Page */
                  role={isMobile ? "status" : undefined}
                  aria-label={isMobile ? (isDirty ? "יש שינויים שלא נשמרו" : "נשמר") : undefined}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "12.5px",
                    whiteSpace: "nowrap",
                    flex: { xs: "none", md: "0 1 auto" },
                    color: isDirty ? v2.warn : v2.ink3,
                    marginInlineStart: { xs: "auto", md: 0 },
                  }}
                >
                  {isDirty && (
                    <Box
                      sx={{ width: "6px", height: "6px", borderRadius: "50%", background: "currentColor", flex: "none" }}
                    />
                  )}
                  {/* on a phone the dot says it, and the button beside it is the one you press */}
                  <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                    {isDirty ? "יש שינויים שלא נשמרו" : "נשמר"}
                  </Box>
                </Box>
                <Box sx={{ marginInlineStart: { xs: 0, md: "auto" }, flex: { xs: "none", md: "0 1 auto" } }}>
                  <PrimaryButton
                    onClick={() => void save()}
                    disabled={!isDirty || saving}
                    sx={{ height: { xs: "38px", md: "36px" } }}
                  >
                    <SaveIcon sx={{ fontSize: 16 }} />
                    {saving ? "שומר…" : "שמור תבנית"}
                  </PrimaryButton>
                </Box>
              </>
            )}
          </Box>

          {/* the targets bar does not pin on a phone, so the one number the template is built
              against rides here instead — see TargetsMiniStrip */}
          {isMobile && builderOpen && !calculatorOpen && (
            <Box sx={{ paddingBottom: "7px" }}>
              <TargetsMiniStrip totals={totals} targets={template.targets} onExpand={scrollToTop} />
            </Box>
          )}
        </Box>

        {/* step 1 — the calculator */}
        {calculatorOpen && (
          <CalculatorPanel
            state={calculator}
            onChange={setCalculator}
            onConfirm={builderOpen ? applyCalculator : () => void createTemplateStep()}
            confirmLabel={builderOpen ? "החל יעדים" : "בנה תבנית"}
            onCancel={builderOpen ? cancelCalculator : undefined}
            /* Skipping is a way to CREATE a template, so it is offered only on the way in. Once the
               template exists the same thing is one button away in the targets bar.

               One entry, not the menu's three: a template is a shape — meals — and the other two
               menu kinds have no shape to save. A macro template would be a number with nothing
               around it, and a PDF is a file that belongs to one trainee's plan. So the button
               goes straight through rather than asking which. */
            skipActions={
              builderOpen
                ? []
                : [{ label: "תבנית מפורטת", onClick: () => void createTemplateStep({ skipCalculator: true }) }]
            }
            subject="template"
            /* A template belongs to no trainee, so its calculator starts empty and often stays
               that way — building one has never required filling it in, and must not start to.
               Applying a calculation to a template that already exists still needs one. */
            requireTdee={builderOpen}
          />
        )}

        {/* step 2 — the builder, counting down to the template's own targets */}
        {builderOpen && (
          /* One pinned block, as on the menu builder: the targets and the actions that change
             them stick together, resting against the measured header rather than under it. */
          <MealsList
            meals={template.meals}
            onChange={(meals) => setTemplate((prev) => ({ ...prev, meals }))}
            onNotify={setNotice}
            stickyTop={headerHeight}
            stickyHeader={
              <TargetsSummaryBar
                totals={totals}
                targets={template.targets}
                /* In follow mode there IS no recommendation: the targets are the template, so a
                   "יעדי התבנית" heading over a line restating the totals says the same thing
                   twice — and on an empty template it says it as a row of zeroes. The chip in the
                   action slot is the whole of what the head has to say there. */
                title={targetsFollowTemplate ? undefined : "יעדי התבנית"}
                goalSummary={targetsFollowTemplate ? undefined : goalSummary}
                action={calculatorOpen ? undefined : targetsActions}
              />
            }
          />
        )}

        <ConfirmDialog
          open={leaveOpen}
          onClose={() => setLeaveOpen(false)}
          title="לצאת בלי לשמור?"
          message="יש בתבנית שינויים שלא נשמרו. אם תצא עכשיו הם יאבדו."
          confirmLabel="צא בלי לשמור"
          cancelLabel="הישאר בתבנית"
          extraAction={{
            label: "שמור וצא",
            // Awaited, and it only leaves if the save actually landed — walking away mid-request is
            // exactly how a save gets abandoned while the trainer believes it was stored.
            onClick: () => {
              void save().then((ok) => {
                if (ok) backToTemplates();
              });
            },
          }}
          onConfirm={backToTemplates}
        />

        {saveError && (
          <BuilderToast tone="error" testId="template-save-error" onDismiss={() => setSaveError("")}>
            {saveError}
          </BuilderToast>
        )}

        {notice && <BuilderToast onDismiss={() => setNotice("")}>{notice}</BuilderToast>}
      </Box>
    </RTL>
  );
};

/**
 * Loads the template, then hands a guaranteed one to the editor.
 *
 * Same split, and the same reason, as the menu builder: the editor seeds its state from what it is
 * given and `useState` initialisers run once, so an editor mounted before the fetch landed would
 * keep the empty template it started with and overwrite the real one on the next save.
 */
const MenuTemplateBuilderPage = () => {
  const navigate = useNavigate();
  const { templateId = "new" } = useParams();
  const isNew = templateId === "new";
  const { template, loading, error } = useMenuTemplateV2(isNew ? undefined : templateId);

  if (loading) {
    return (
      <RTL>
        <Box
          data-testid="template-loading"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            minHeight: "60vh",
          }}
        >
          <CircularProgress size={28} sx={{ color: v2.brand }} />
          <Box sx={{ fontSize: 13, color: v2.ink3 }}>טוען תבנית…</Box>
        </Box>
      </RTL>
    );
  }

  // A template that will not load must not fall through to a blank builder — the trainer would
  // start editing what looks like their template and save an empty one over it.
  if (!isNew && (error || !template)) {
    return (
      <RTL>
        <Box
          data-testid="template-load-error"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            minHeight: "60vh",
          }}
        >
          <Box sx={{ fontSize: 15, fontWeight: 600, color: v2.over }}>לא הצלחנו לטעון את התבנית</Box>
          <Box sx={{ fontSize: 13, color: v2.ink3 }}>ייתכן שהתבנית נמחקה, או שאין חיבור לשרת</Box>
          <TextButton onClick={() => navigate(TEMPLATES)}>חזרה לתבניות</TextButton>
        </Box>
      </RTL>
    );
  }

  return <MenuTemplateBuilderEditor existing={template} />;
};

export default MenuTemplateBuilderPage;
